/**
 * Typed contract for the CTX-96/CTX-99 `chopper-backend` Insight API
 * (`/api/v2/insight/*`, see `docs/insight-api-contract.md` on
 * `daniel/ctx-96-expose-tenant-authorized-insight-apis-through-chopper`, PR
 * #1238, superseded by the recovered dev-based branch in CTX-99 PR #1239).
 *
 * The backend is the only thing the browser talks to — it never hands the
 * frontend a bucket name, S3 key, or the internal insight-data-service URL,
 * and tenant scope is always resolved server-side. Nothing in this file (or
 * anything that consumes it) should ever accept a tenant id, bucket, or key
 * from user input.
 *
 * `sourceId` is always the canonical opaque identifier resolved by
 * `canonicalSourceId()` in `../utils/insightScopeTree.ts` (the BGP sidebar's
 * `subId`, falling back to `id`) — never the source's incremental display
 * id. See that module's doc comment for the CTX-97 "real-data milestone"
 * identity-contract note.
 */

export type InsightSourceType = "reflector" | "cluster";

/** `manifestToMemberStatus` in `InsightController.js` — per-member processing state. */
export type InsightMemberStatus =
	| "completed"
	| "completed_with_warnings"
	| "deleted"
	| "failed"
	| "unknown"
	| "missing"
	| "unavailable"
	| "timeout"
	| "not_found"
	| "invalid_request"
	| "upstream_error";

export const INSIGHT_COMPLETED_STATUSES: ReadonlySet<InsightMemberStatus> = new Set([
	"completed",
	"completed_with_warnings",
]);

export type InsightReflectorDeviceStatus = "online" | "offline" | "unknown";
export type InsightClusterDeviceStatus = "Online" | "Degraded" | "Offline" | "Invalid";

export interface InsightReflectorSourceItem {
	/** Canonical CTXR UUID (BGP sidebar's `subId`, falling back to its display id) — never the source's incremental display id. */
	sourceId: string;
	sourceType: "reflector";
	name: string;
	deviceStatus: InsightReflectorDeviceStatus;
	hasDiscoverySource: boolean;
}

export interface InsightClusterSourceItem {
	/** Canonical cluster id authorized server-side — the client never submits a member array. */
	sourceId: string;
	sourceType: "cluster";
	name: string;
	cloud: string | null;
	region: string | null;
	deviceStatus: InsightClusterDeviceStatus;
	memberCount: number;
}

export type InsightSourceItem = InsightReflectorSourceItem | InsightClusterSourceItem;

export interface InsightPagination {
	limit: number;
	nextCursor: string | null;
	total?: number;
}

export interface InsightEnvelope<T> {
	success: boolean;
	message: string;
	data: T;
	pagination?: InsightPagination;
}

export interface InsightScopeTuple {
	provider: string;
	region: string;
	account: string | null;
}

export interface InsightScopeMember {
	ctxrUuid: string;
	memberSourceId: number;
	name: string;
	status: InsightMemberStatus;
	lastProcessedAt: string | null;
}

export interface InsightTopologyScopesResponse {
	sourceType: InsightSourceType;
	sourceId: string;
	scopes: InsightScopeTuple[];
	members: InsightScopeMember[];
	partial: boolean;
	state: "ok" | "partial";
}

/** Digest node category — `"group"` is a synthetic ELK presentation boundary (see `groupKind`); `"resource"` is a real discovered resource (see `type`). */
export type InsightGraphNodeKind = "group" | "resource";

/**
 * CTX-113 versioned semantic presentation contract identifier
 * (`InsightTopologyResponse.profile`). Bumped whenever insight-data-service
 * changes the node/decoration shape in a way older frontend code can't
 * safely assume (e.g. moving NACL/SG/route-table association data from
 * "reconstruct it from `edges`" to "read it off `data` directly").
 *
 * `"1.0.0"` is the pre-CTX-113 shape this app used to render generically
 * (every edge drawn as a line, no `effectiveNacl`/`securityGroups`/
 * `resolvedInstance` decorations) — it is deliberately NOT in
 * `SUPPORTED_INSIGHT_TOPOLOGY_PROFILES` below, so a response still on that
 * profile is rejected with an explicit "unsupported version" state instead
 * of being rendered with the wrong (V2) node/chip assumptions.
 */
export const INSIGHT_TOPOLOGY_PROFILE_V2 = "2.0.0" as const;

/** Every `InsightTopologyResponse.profile` value this frontend knows how to render. */
export const SUPPORTED_INSIGHT_TOPOLOGY_PROFILES: ReadonlySet<string> = new Set([
	INSIGHT_TOPOLOGY_PROFILE_V2,
]);

export function isSupportedInsightTopologyProfile(profile: unknown): boolean {
	return typeof profile === "string" && SUPPORTED_INSIGHT_TOPOLOGY_PROFILES.has(profile);
}

/**
 * A server-resolved reference to a related resource, used for the CTX-113
 * decoration fields below (`effectiveNacl`, `securityGroups`,
 * `resolvedInstance`). Always produced by insight-data-service — the
 * frontend must render these as-is and never reconstruct an equivalent
 * mapping itself by classifying `InsightGraphEdge`s in the browser.
 */
export interface InsightChipRef {
	id: string;
	label: string;
	/** Cloud-native id, when it differs from `label` (e.g. `acl-0abc123`). */
	nativeId?: string;
}

/**
 * CTX-113 decoration fields insight-data-service attaches directly to a
 * subnet resource node's `data`. `effectiveNacl` is the subnet's single
 * resolved effective NACL (AWS) / security list (OCI) — the backend already
 * picked "the" effective one, so the frontend renders at most one chip, not
 * a list.
 *
 * `effectiveRouteTable` (CTX-128) is the subnet's single resolved effective
 * route table, rendered as a compact chip centered at the top of the subnet
 * card — never a derived line to the route-table shelf. Optional/additive on
 * the same `"2.0.0"` profile: a payload predating this field simply renders
 * no route-table chip, the same graceful-degradation treatment already
 * established for `effectiveNacl`.
 */
export interface InsightSubnetDecorations {
	effectiveNacl?: InsightChipRef | null;
	effectiveRouteTable?: InsightChipRef | null;
}

/**
 * CTX-113 decoration fields insight-data-service attaches directly to an
 * ENI/VNIC resource node's `data`. `securityGroups` is already deduplicated
 * server-side; `resolvedInstance` is the owning instance the backend was
 * able to resolve, if any.
 *
 * CTX-128 additions:
 * - `status` — a coarse health signal (e.g. an ENI in an error/detached
 *   state) surfaced on the card itself and rolled up into the collapsed
 *   "+N ENIs" subnet summary's warning/error counts.
 * - `securityGroupsUnavailable` — set when insight-data-service could not
 *   resolve this ENI's Security Groups (most commonly a missing
 *   `DescribeSecurityGroups`-class permission) so the card can say so
 *   honestly instead of silently rendering zero SG chips, which reads
 *   identically to "this ENI genuinely has no Security Groups."
 */
export interface InsightEniDecorations {
	securityGroups?: InsightChipRef[];
	resolvedInstance?: InsightChipRef | null;
	status?: "ok" | "warning" | "error";
	securityGroupsUnavailable?: boolean;
}

/**
 * CTX-128 — a proven connectivity target rendered above its route table
 * (IGW, egress-only IGW, NAT Gateway, TGW, VGW, VPC Peering, applicable
 * endpoints, or a proven Direct Connect chain the processor already
 * resolved end-to-end). `kind` drives the chip's icon; `routeCount` is the
 * number of the route table's individual routes this one overview
 * relationship aggregates — never rendered as one line per route.
 */
export interface InsightConnectivityRef extends InsightChipRef {
	kind: "igw" | "egress-only-igw" | "nat" | "tgw" | "vgw" | "peering" | "endpoint" | "direct-connect" | "eni" | "other";
	routeCount?: number;
}

/** A single individual route, only ever exposed in the route table's own detail — the overview card shows `routeCount`/`connectivity`, never one line per route. */
export interface InsightRouteEntry {
	destination: string;
	targetLabel: string;
	targetId?: string;
}

/**
 * CTX-128 decoration fields insight-data-service attaches directly to a
 * route-table resource node's `data`. `connectivity` is at most one entry
 * per unique route-table/target pair (already deduplicated server-side);
 * `routeCount` is the table's total individual-route count, shown as a
 * single number on the card rather than a per-route line. `routes` is the
 * full individual-route list, rendered only in `ResourceDetailPanel` once
 * the route table itself is selected.
 */
export interface InsightRouteTableDecorations {
	routeCount?: number;
	connectivity?: InsightConnectivityRef[];
	routes?: InsightRouteEntry[];
}

/**
 * A ReactFlow-shaped node/edge as produced by insight-data-service (server-side
 * ELK layout), plus contributing-CTXR provenance.
 *
 * `position` is always relative to `parentId` per the digest contract — the
 * frontend must never convert it to root/absolute coordinates itself
 * (CTX-108). `parentId`/`extent`/`width`/`height` are the containment fields
 * ELK computes for VPC/region/account/availability-zone boundaries; dropping
 * them is what collapsed the whole graph into an unreadable flat band.
 */
export interface InsightGraphNode {
	id: string;
	/** `"group"` for synthetic presentation boundaries, `"resource"` for discovered resources. Absent on older/partial payloads. */
	kind?: InsightGraphNodeKind;
	/** Only set when `kind === "group"` — one of the digest's presentation groups (`providerPartition` | `region` | `availabilityGroup`). */
	groupKind?: string;
	/** Resource type when `kind === "resource"` (e.g. `instance`, `vpc`, `subnet`, `sg`...). */
	type?: string;
	/** Relative to `parentId` — never root/absolute coordinates. */
	position?: { x: number; y: number };
	/** Containing node's id in the server's ELK containment hierarchy. Must resolve to another node in the same response. */
	parentId?: string;
	/** ELK-calculated clamp — `"parent"` constrains this node's position/drag to stay within `parentId`'s bounds. */
	extent?: "parent" | [[number, number], [number, number]];
	/** ELK-calculated footprint in px. Required for group/container nodes to render at their real size instead of collapsing to auto-size. */
	width?: number;
	height?: number;
	/**
	 * `label`/`nativeId`/`cidr` plus, per resource kind, the CTX-113
	 * decoration fields above (`InsightSubnetDecorations` on a subnet,
	 * `InsightEniDecorations` on an eni/vnic) — kept as a loose
	 * `Record<string, unknown>` (not a discriminated union) because a
	 * single digest mixes many resource kinds in one flat node array.
	 */
	data?: Record<string, unknown>;
	style?: Record<string, unknown>;
	ctxrUuids: string[];
	[key: string]: unknown;
}

export interface InsightGraphEdge {
	id: string;
	source: string;
	target: string;
	type?: string;
	data?: Record<string, unknown>;
	style?: Record<string, unknown>;
	/** ELK-routed polyline waypoints. Preserved end-to-end even though this fix does not yet render a routed path — see `toReactFlowEdges` in `InsightTopologyCanvas.tsx` for the deliberate fallback. */
	points?: Array<{ x: number; y: number }>;
	ctxrUuids: string[];
	[key: string]: unknown;
}

export interface InsightUnavailableMember {
	memberSourceId: number;
	name: string;
	reason: string;
}

/**
 * CTX-128 — a non-fatal Discovery/CSPM coverage gap on an otherwise-usable
 * topology (e.g. the processing role lacks `DescribeSecurityGroups` for one
 * account/region). Distinct from `InsightUnavailableMember` (a whole member
 * that failed to produce topology at all): a coverage warning still ships a
 * renderable topology, just missing one resource family's data for the named
 * scope. `message` is a curated, user-safe description — this must never
 * carry a raw internal/stack-trace error string. `ctxrUuids`, when present,
 * scopes the warning to specific cluster member(s) so a healthy member's
 * topology/warnings stay independent of a degraded sibling's.
 */
export interface InsightCoverageWarning {
	id: string;
	account: string | null;
	region: string | null;
	/** The resource family this gap affects, e.g. "SecurityGroup", "Nacl", "RouteTable". */
	resourceFamily: string;
	/** Human-readable capability that's unavailable, e.g. "Security Group enumeration". */
	capability: string;
	message: string;
	/** Optional non-sensitive remediation hint (e.g. which IAM action/role to grant), never a raw error. */
	remediation?: string;
	ctxrUuids?: string[];
}

export interface InsightProvenanceMember {
	ctxrUuid: string;
	memberSourceId: number;
	name: string;
	status: InsightMemberStatus;
}

export interface InsightTopologyResponse {
	sourceType: InsightSourceType;
	sourceId: string;
	cloud: string;
	region: string;
	account: string | null;
	/**
	 * CTX-113 versioned semantic presentation contract identifier (see
	 * `SUPPORTED_INSIGHT_TOPOLOGY_PROFILES`). `InsightTopologyCanvas` checks
	 * this before rendering and shows an explicit "unsupported version"
	 * state rather than guessing at an incompatible node/decoration shape.
	 *
	 * CTX-408: `null` is a value the API really sends, and CTX-409 reserves it
	 * to mean version resolution failed — so it is modelled here rather than
	 * cast away at the call sites that have always handled it.
	 */
	profile: string | null;
	nodes: InsightGraphNode[];
	/**
	 * Kept for a future focused-path/traffic mode (out of scope for the
	 * default inventory overview) — `InsightTopologyCanvas` never renders
	 * these by default per CTX-113's "no automatic relationship lines"
	 * policy. NACL/SG/route-table associations are read from each node's
	 * own `data` decorations instead (see `InsightSubnetDecorations`/
	 * `InsightEniDecorations`), never reconstructed from this list.
	 */
	edges: InsightGraphEdge[];
	provenance: InsightProvenanceMember[];
	unavailableMembers: InsightUnavailableMember[];
	/**
	 * CTX-128 — non-fatal Discovery/CSPM coverage gaps (see
	 * `InsightCoverageWarning`). Optional/additive: absent or empty on a
	 * fully-covered digest, and on any payload predating this field.
	 */
	coverageWarnings?: InsightCoverageWarning[];
	partial: boolean;
	/**
	 * CTX-409 widened this beyond `ok`/`partial` so a caller can tell *why* a
	 * result is empty instead of inferring it from `nodes.length` (CTX-422):
	 *
	 * - `ok` — a scope-matching digest was read and carried a topology
	 *   artifact. Zero nodes under `ok` means the scope is genuinely empty.
	 * - `partial` — some members are unavailable; they are in
	 *   `unavailableMembers`. Takes precedence over the two below.
	 * - `not_processed` — no member contributed a digest for the requested
	 *   scope: none registered, or every digest describes a different one.
	 * - `topology_unavailable` — a scope-matching digest was read, but no
	 *   topology artifact came with it.
	 *
	 * Kept as a plain union, like `InsightProcessSnapshotsResponse.state`
	 * below. An unrecognised future value must degrade to the empty state,
	 * never to a blank canvas.
	 */
	state: "ok" | "partial" | "not_processed" | "topology_unavailable";
}

export interface InsightSecurityFinding {
	ctxrUuid: string;
	[key: string]: unknown;
}

export interface InsightSecuritySummary {
	critical: number;
	high: number;
	medium: number;
	low: number;
	total: number;
}

export interface InsightOptimizationFinding {
	ctxrUuid: string;
	[key: string]: unknown;
}

export interface InsightFindingsResponse {
	sourceType: InsightSourceType;
	sourceId: string;
	cloud: string;
	region: string;
	account: string | null;
	matched: boolean;
	security: {
		score: number | null;
		summary: InsightSecuritySummary;
		findings: InsightSecurityFinding[];
	};
	optimization: {
		totalMonthlySavingsUsd: number;
		findings: InsightOptimizationFinding[];
	};
	unavailableMembers: InsightUnavailableMember[];
	partial: boolean;
	state: "ok" | "partial";
}

export interface InsightDiscoverySourceItem {
	ctxrUuid: string;
	lastModified: string | null;
	sizeBytes: number | null;
	reflector: { sourceId: string; name: string } | null;
}

export interface InsightProcessSnapshotResult {
	memberSourceId: number;
	name: string;
	ctxrUuid: string;
	runId?: string;
	status: string;
	warnings: string[];
}

export interface InsightProcessSnapshotsResponse {
	sourceType: InsightSourceType;
	sourceId: string;
	state: "ok" | "partial" | "no_sources";
	results: InsightProcessSnapshotResult[];
	partial?: boolean;
}

export interface InsightCurrentSnapshotMember {
	memberSourceId: number;
	name: string;
	status: InsightMemberStatus;
	generationId: string | null;
	completedAt: string | null;
	resourceCount: number;
}

export interface InsightCurrentSnapshotResponse {
	sourceType: InsightSourceType;
	sourceId: string;
	members: InsightCurrentSnapshotMember[];
	partial: boolean;
	state: "ok" | "partial";
}

/**
 * Client-side request/response state shared by every Insight surface
 * (`useInsightAsync`). Distinguishes a request that hasn't run yet from one
 * that succeeded with no data, and carries the HTTP-mapped error state
 * (`unauthorized` / `service-down` / `not-found` / `error`) so the UI can
 * render a specific, non-blanking status instead of a generic failure.
 */
export type InsightRequestState =
	| "idle"
	| "loading"
	| "success"
	| "empty"
	| "unauthorized"
	| "not-found"
	| "service-down"
	| "error";
